// v3.21-1687 · Financial Integrity Resolution Center — SSOT VIEW LAYER.
//
// Changes vs v3.18-1312:
//   * Removed the ``rebuild_ledger`` repair action.  This diagnostic
//     is strictly a VIEW/AUDIT layer; ledger corrections must occur at
//     the authoritative payout/ledger workflow, not from this screen.
//   * "Financial Impact" tile renamed to "Total Discrepancy Amount" —
//     the underlying metric is SUM(|difference|) which is a
//     diagnostic sum, not authoritative company financial loss.
//   * Version label reads from version.json (Hub SSOT) rather than an
//     obsolete embedded module version.
//
// Comparison contract (unchanged; documented for the audit trail):
//   • payroll_commissions = authoritative Compensation Engine result
//   • ptc_ledger          = authoritative payout / commission ledger
//   • COMMISSION_MISMATCH        — engine total ≠ ledger total
//   • COMMISSION_NOT_IN_LEDGER   — engine total > 0 but ledger total 0
// Deal identity is stable ``deal.id`` throughout; Deal amount is
// canonical ``deal.deal_amount``.  This screen NEVER independently
// recalculates compensation — it compares the two authoritative sides.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2,
  RefreshCw, ExternalLink, Wrench, Search, Filter, GitCompare, Eye,
} from 'lucide-react';

const SEVERITY_STYLE = {
  red:    { border: 'border-red-300',    bg: 'bg-red-50',     text: 'text-red-700',    label: 'Critical' },
  yellow: { border: 'border-amber-300',  bg: 'bg-amber-50',   text: 'text-amber-700',  label: 'Warning'  },
  green:  { border: 'border-emerald-300',bg: 'bg-emerald-50', text: 'text-emerald-700',label: 'Minor'    },
};

const STATUS_STYLE = {
  open:                  'bg-red-100 text-red-800',
  investigating:         'bg-blue-100 text-blue-800',
  reviewed:              'bg-indigo-100 text-indigo-800',
  resolved:              'bg-emerald-100 text-emerald-800',
  ignored:               'bg-slate-200 text-slate-700',
  requires_development:  'bg-purple-100 text-purple-800',
};

// Every action label describes exactly what it will do.  No silent
// synchronization.  The tool never assumes which side is authoritative.
// v3.21-1687 · ``rebuild_ledger`` REMOVED — this diagnostic is a VIEW
// layer and must not write directly to ptc_ledger.  Ledger corrections
// happen at the authoritative payout/ledger workflow, not here.
const REPAIR_ACTIONS = [
  { key: 'recalculate_compensation',
    label: 'Recalculate Compensation Engine',
    prompt: 'Re-runs the compensation calculator against this deal and reports the freshly computed totals. Read-only — nothing is written to payroll or the ledger.' },
  { key: 'rerun_audit',
    label: 'Refresh Financial Audit',
    prompt: 'Re-runs the mismatch audit ONLY for this deal and reloads the card. Diagnostic-only — never writes to payroll_commissions or ptc_ledger. If the difference disappears, the issue is auto-resolved.' },
];

const CONFIDENCE_STYLE = (pct) => {
  const n = Number(pct || 0);
  if (n >= 85) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (n >= 60) return 'bg-blue-100    text-blue-800    border-blue-300';
  if (n >= 30) return 'bg-amber-100   text-amber-800   border-amber-300';
  return          'bg-slate-100   text-slate-700   border-slate-300';
};

const fmtMoney = (n) => {
  const x = Number(n || 0);
  const sign = x < 0 ? '-' : (x > 0 ? '' : '');
  return `${sign}$${Math.abs(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric',
                                        hour: 'numeric', minute: '2-digit' });
  } catch { return String(iso).replace('T', ' ').slice(0, 16); }
};

function SummaryTile({ label, value, tone = 'slate', testId }) {
  const t = {
    slate:  'bg-slate-50 border-slate-200 text-slate-800',
    red:    'bg-red-50 border-red-200 text-red-800',
    amber:  'bg-amber-50 border-amber-200 text-amber-800',
    emerald:'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue:   'bg-blue-50 border-blue-200 text-blue-800',
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${t}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

/**
 * Side-by-side payroll vs ledger diff modal.
 *
 * Rows are aligned by normalized label.  Rows only present on one side
 * render as an empty cell on the opposite side with the missing side
 * highlighted in red.  Rows present on both sides with different
 * amounts are highlighted in amber.  Matching rows are neutral.
 */
function DifferenceModal({ open, onOpenChange, issue }) {
  const rows = useMemo(() => {
    if (!issue) return [];
    const groupBy = (arr) => {
      const out = {};
      (arr || []).forEach(e => {
        const k = (e.label || e.event || 'unlabelled').trim().toLowerCase() || 'unlabelled';
        (out[k] = out[k] || []).push(e);
      });
      return out;
    };
    const p = groupBy(issue.payroll_events);
    const l = groupBy(issue.ledger_events);
    const keys = Array.from(new Set([...Object.keys(p), ...Object.keys(l)])).sort();
    return keys.map(k => {
      const pe = p[k] || [];
      const le = l[k] || [];
      const psum = pe.reduce((s, e) => s + Number(e.amount || 0), 0);
      const lsum = le.reduce((s, e) => s + Number(e.amount || 0), 0);
      let kind = 'match';
      if (pe.length && !le.length) kind = 'missing_ledger';
      else if (!pe.length && le.length) kind = 'missing_payroll';
      else if (Math.abs(psum - lsum) > 0.01) kind = 'amount_diff';
      return {
        key: k,
        label: (pe[0]?.label || le[0]?.label || k).toString(),
        payroll: pe,
        ledger:  le,
        payroll_total: psum,
        ledger_total:  lsum,
        kind,
      };
    });
  }, [issue]);

  if (!issue) return null;
  const t = issue.totals || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid={`fi-diff-modal-${issue.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-slate-600" />
            Payroll vs Ledger — {issue.deal?.client_name || `Deal ${(issue.deal?.id || '').slice(0, 8)}`}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            Side-by-side comparison. Red rows are missing on that side, amber rows have amount mismatches, neutral rows match.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 px-1">
          <div>Payroll</div>
          <div>Ledger</div>
        </div>

        <div className="border rounded max-h-[60vh] overflow-y-auto" data-testid={`fi-diff-modal-body-${issue.id}`}>
          {rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 italic">Nothing to compare.</div>
          ) : rows.map((r) => {
            const missingPayroll = r.kind === 'missing_payroll';
            const missingLedger  = r.kind === 'missing_ledger';
            const amountDiff     = r.kind === 'amount_diff';
            const pClass = missingPayroll ? 'bg-red-50'    : (amountDiff ? 'bg-amber-50' : 'bg-white');
            const lClass = missingLedger  ? 'bg-red-50'    : (amountDiff ? 'bg-amber-50' : 'bg-white');
            return (
              <div key={r.key} className="grid grid-cols-2 border-t"
                   data-testid={`fi-diff-row-${r.key}`}>
                <div className={`p-2 border-r ${pClass}`}>
                  {missingPayroll ? (
                    <div className="text-xs italic text-red-700">— missing —</div>
                  ) : r.payroll.map((e, i) => (
                    <div key={e.id || i} className="text-xs flex justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{e.label}</div>
                        <div className="text-[10px] text-slate-500">Generated {fmtWhen(e.created_at)}</div>
                      </div>
                      <div className={`font-mono font-semibold ${amountDiff ? 'text-amber-700' : 'text-slate-800'}`}>
                        {fmtMoney(e.amount)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`p-2 ${lClass}`}>
                  {missingLedger ? (
                    <div className="text-xs italic text-red-700">— missing —</div>
                  ) : r.ledger.map((e, i) => (
                    <div key={e.id || i} className="text-xs flex justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{e.label}</div>
                        <div className="text-[10px] text-slate-500">Generated {fmtWhen(e.created_at)}</div>
                      </div>
                      <div className={`font-mono font-semibold ${amountDiff ? 'text-amber-700' : 'text-slate-800'}`}>
                        {fmtMoney(e.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Totals row */}
          <div className="grid grid-cols-2 border-t-2 border-slate-800 bg-slate-100 font-semibold">
            <div className="p-2 border-r flex justify-between text-xs">
              <span>Payroll Total</span>
              <span className="font-mono">{fmtMoney(t.payroll)}</span>
            </div>
            <div className="p-2 flex justify-between text-xs">
              <span>Ledger Total</span>
              <span className="font-mono">{fmtMoney(t.ledger)}</span>
            </div>
          </div>
        </div>

        <div className="text-sm text-slate-700 border-t pt-2 flex items-center justify-between">
          <span>Difference:</span>
          <span className={`font-mono font-bold ${Math.abs(t.difference || 0) > 0.01 ? 'text-red-700' : 'text-emerald-700'}`}
                data-testid={`fi-diff-modal-total-${issue.id}`}>
            {Number(t.difference || 0) > 0 ? '+' : ''}{fmtMoney(t.difference)}
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`fi-diff-modal-close-${issue.id}`}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueCard({ issue, onChange }) {
  const [open, setOpen]           = useState(false);
  const [busy, setBusy]           = useState(false);
  const [confirm, setConfirm]     = useState(null); // action pending confirmation
  const [reason, setReason]       = useState('');
  const [ignoreReason, setIgnoreReason]     = useState('');
  const [resolveMethod, setResolveMethod]   = useState('');
  const [reviewNotes,   setReviewNotes]     = useState('');
  const [diffOpen, setDiffOpen]             = useState(false);

  const sev = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.yellow;
  const t   = issue.totals || {};
  const diff = Number(t.difference || 0);
  const confidence = Number(issue.cause_confidence || 0);

  const runRepair = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/financial-integrity/issues/${issue.id}/repair`, {
        action, confirmed: true, reason: reason.trim() || null,
      });
      toast.success(
        `${action} complete${data.issue_removed ? ' — issue resolved automatically.' : '.'}`
      );
      setConfirm(null); setReason('');
      onChange();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Repair failed.'));
    } finally { setBusy(false); }
  };

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/admin/financial-integrity/issues/${issue.id}`, { status, ...extra });
      toast.success(`Marked as ${status.replace('_', ' ')}.`);
      setIgnoreReason(''); setResolveMethod(''); setReviewNotes('');
      onChange();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Status update failed.'));
    } finally { setBusy(false); }
  };

  return (
    <Card className={`${sev.border} border-2`} data-testid={`fi-issue-${issue.id}`}>
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen(v => !v)}
        data-testid={`fi-issue-toggle-${issue.id}`}
      >
        <CardHeader className={`${sev.bg} py-3 px-4`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <Badge className={sev.text + ' bg-white/70 border ' + sev.border}>
                {sev.label}
              </Badge>
              <Badge variant="outline" className="font-mono text-[10px]">
                {issue.issue_type}
              </Badge>
              <Badge className={STATUS_STYLE[issue.status] || STATUS_STYLE.open}>
                {(issue.status || 'open').replace('_', ' ')}
              </Badge>
              <div className="truncate text-sm font-semibold text-slate-900">
                {issue.deal?.client_name || `Deal ${(issue.deal?.id || '').slice(0, 8)}`}
              </div>
              {issue.company?.name && (
                <span className="text-xs text-slate-500">· {issue.company.name}</span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">Deal:</span>
              <span className="font-semibold">{fmtMoney(issue.deal?.amount)}</span>
              <span className={`font-bold ${diff > 0 ? 'text-red-600' : (diff < 0 ? 'text-amber-600' : 'text-slate-600')}`}>
                Δ {diff > 0 ? '+' : ''}{fmtMoney(diff)}
              </span>
            </div>
          </div>
          <div className="mt-1 text-[11px] text-slate-600 flex flex-wrap gap-3">
            {issue.assigned?.full_name && <span>Assigned: <b>{issue.assigned.full_name}</b></span>}
            <span>Detected: {fmtWhen(issue.detected_at)}</span>
            <span>Last seen: {fmtWhen(issue.last_seen_at)}</span>
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="p-4 space-y-4">
          {/* Totals */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded border p-3 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Payroll</div>
              <div className="text-xl font-bold" data-testid={`fi-payroll-total-${issue.id}`}>{fmtMoney(t.payroll)}</div>
            </div>
            <div className="rounded border p-3 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Ledger</div>
              <div className="text-xl font-bold" data-testid={`fi-ledger-total-${issue.id}`}>{fmtMoney(t.ledger)}</div>
            </div>
            <div className={`rounded border-2 p-3 ${diff !== 0 ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Difference</div>
              <div className={`text-xl font-bold ${diff > 0 ? 'text-red-700' : (diff < 0 ? 'text-amber-700' : 'text-emerald-700')}`}
                   data-testid={`fi-diff-${issue.id}`}>
                {diff > 0 ? '+' : ''}{fmtMoney(diff)}
              </div>
            </div>
          </div>

          {/* Likely cause + Confidence */}
          <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold pt-0.5">Likely Cause</span>
              <span className="text-blue-900 flex-1 min-w-[240px]" data-testid={`fi-cause-${issue.id}`}>
                {issue.likely_cause}
              </span>
              <Badge className={`border ${CONFIDENCE_STYLE(confidence)}`}
                     data-testid={`fi-confidence-${issue.id}`}>
                Confidence: {confidence}%
              </Badge>
            </div>
          </div>

          {/* Exact mismatched compensation events */}
          {(issue.diffs || []).length > 0 && (
            <div className="rounded border p-2 bg-white">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
                Mismatched Compensation Events
              </div>
              <div className="space-y-2">
                {issue.diffs.map((d, i) => {
                  const missingPayroll = d.kind === 'missing_payroll';
                  const missingLedger  = d.kind === 'missing_ledger';
                  return (
                    <div key={i}
                         className={`rounded border p-2 ${missingPayroll || missingLedger ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
                         data-testid={`fi-event-mismatch-${issue.id}-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                          Compensation Event
                        </div>
                        <Badge variant="outline" className="text-[9px] font-mono">
                          {d.kind.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{d.label}</div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Generated</div>
                          <div className="font-medium">{fmtWhen(d.generated_at)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Expected Amount</div>
                          <div className="font-mono font-semibold">{fmtMoney(d.expected_amount)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Payroll</div>
                          <div className={`font-semibold ${d.payroll_present ? 'text-slate-800' : 'text-red-700'}`}>
                            {d.payroll_present ? fmtMoney(d.payroll) : 'Missing'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Ledger</div>
                          <div className={`font-semibold ${d.ledger_present ? 'text-slate-800' : 'text-red-700'}`}>
                            {d.ledger_present ? fmtMoney(d.ledger) : 'Missing'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Deep-links */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setDiffOpen(true)}
                    data-testid={`fi-view-diff-${issue.id}`}>
              <GitCompare className="h-3.5 w-3.5 mr-1" /> View Difference
            </Button>
            {issue.deal?.id && (
              <Link to={`/clients/${issue.deal.id}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid={`fi-view-deal-${issue.id}`}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> View Deal
                </Button>
              </Link>
            )}
            {issue.assigned?.id && (
              <Link to={`/hr/${issue.assigned.id}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Employee
                </Button>
              </Link>
            )}
            {issue.company?.id && (
              <Link to={`/companies/${issue.company.id}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Company
                </Button>
              </Link>
            )}
          </div>

          {/* Repair actions — explicit, no silent syncing */}
          <div className="border-t pt-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold flex items-center gap-1">
              <Wrench className="h-3.5 w-3.5" /> Repair actions (every action is audited; nothing runs without confirmation)
            </div>
            {!confirm ? (
              <div className="flex flex-wrap gap-2">
                {REPAIR_ACTIONS.map(a => (
                  <Button key={a.key} size="sm" variant="secondary"
                          onClick={() => setConfirm(a)} disabled={busy}
                          data-testid={`fi-repair-${a.key}-${issue.id}`}>
                    {a.label}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2"
                   data-testid={`fi-repair-confirm-panel-${issue.id}`}>
                <div className="flex items-start gap-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Confirm: {confirm.label}</div>
                    <div className="text-xs mt-1">{confirm.prompt}</div>
                  </div>
                </div>
                <Textarea rows={2}
                          value={reason} onChange={e => setReason(e.target.value)}
                          placeholder="Optional reason (recorded to the audit trail)"
                          data-testid={`fi-repair-reason-${issue.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => runRepair(confirm.key)} disabled={busy}
                          data-testid={`fi-repair-confirm-${issue.id}`}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    Yes, run {confirm.label}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setConfirm(null); setReason(''); }} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Status transitions */}
          <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="uppercase tracking-wider text-slate-500 mb-1 font-semibold">Mark as Investigating</div>
              <div className="text-[11px] text-slate-500 mb-1">Signals to other admins that you have picked this up.</div>
              <Button size="sm" variant="outline" onClick={() => setStatus('investigating')} disabled={busy}
                      data-testid={`fi-status-investigating-${issue.id}`}>
                Set Investigating
              </Button>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500 mb-1 font-semibold flex items-center gap-1">
                <Eye className="h-3 w-3" /> Mark as Reviewed (notes required)
              </div>
              <div className="text-[11px] text-slate-500 mb-1">
                The administrator investigated the issue and determined <b>no repair is required</b> at this time.
              </div>
              <Input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                     placeholder="Notes explaining why no action is being taken"
                     data-testid={`fi-review-notes-${issue.id}`} />
              <Button className="mt-1" size="sm" variant="outline" disabled={busy || !reviewNotes.trim()}
                      onClick={() => setStatus('reviewed', { reason: reviewNotes.trim() })}
                      data-testid={`fi-status-reviewed-${issue.id}`}>
                Mark Reviewed
              </Button>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500 mb-1 font-semibold">Resolve (method required)</div>
              <Input value={resolveMethod} onChange={e => setResolveMethod(e.target.value)}
                     placeholder="e.g. manual rebuild + payroll sync"
                     data-testid={`fi-resolve-method-${issue.id}`} />
              <Button className="mt-1" size="sm" variant="default" disabled={busy || !resolveMethod.trim()}
                      onClick={() => setStatus('resolved', { resolution_method: resolveMethod.trim() })}
                      data-testid={`fi-status-resolved-${issue.id}`}>
                Mark Resolved
              </Button>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500 mb-1 font-semibold">Ignore (reason required)</div>
              <Input value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)}
                     placeholder="Reason (recorded in the audit trail)"
                     data-testid={`fi-ignore-reason-${issue.id}`} />
              <Button className="mt-1" size="sm" variant="ghost" disabled={busy || !ignoreReason.trim()}
                      onClick={() => setStatus('ignored', { reason: ignoreReason.trim() })}
                      data-testid={`fi-status-ignored-${issue.id}`}>
                Ignore
              </Button>
            </div>
          </div>

          {(issue.resolution_method || issue.ignored_reason || issue.review_notes) && (
            <div className="border-t pt-2 text-xs text-slate-500 space-y-1">
              {issue.resolution_method && <div>Resolved via <b>{issue.resolution_method}</b>
                {issue.resolved_by?.name ? ` by ${issue.resolved_by.name}` : ''}
                {issue.resolved_at ? ` at ${fmtWhen(issue.resolved_at)}` : ''}.</div>}
              {issue.review_notes && <div>Reviewed — no repair required. Notes: <b>{issue.review_notes}</b>
                {issue.reviewed_by?.name ? ` (by ${issue.reviewed_by.name}` : ''}
                {issue.reviewed_at ? ` on ${fmtWhen(issue.reviewed_at)})` : (issue.reviewed_by?.name ? ')' : '')}.</div>}
              {issue.ignored_reason && <div>Ignored: <b>{issue.ignored_reason}</b></div>}
            </div>
          )}

          <DifferenceModal open={diffOpen} onOpenChange={setDiffOpen} issue={issue} />
        </CardContent>
      )}
    </Card>
  );
}

export default function FinancialIntegrityCenter() {
  const [issues,   setIssues]   = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [running,  setRunning]  = useState(false);
  const [filters,  setFilters]  = useState({ status: 'open', severity: '', issue_type: '' });

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/financial-integrity/summary');
      setSummary(data);
    } catch (e) { /* summary is nice-to-have */ }
  }, []);

  const loadIssues = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const params = { refresh, ...filters };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const { data } = await api.get('/admin/financial-integrity/issues', { params });
      setIssues(data.items || []);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Could not load issues.'));
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadSummary(); loadIssues(false); }, [loadSummary, loadIssues]);

  const rerun = async () => {
    setRunning(true);
    await loadIssues(true);
    await loadSummary();
    setRunning(false);
    toast.success('Financial audit re-run complete.');
  };

  return (
    <div className="space-y-4" data-testid="financial-integrity-center">
      {/* v3.21-1687 · SSOT disclosure banner — makes the comparison
          contract explicit so operators understand this screen is a
          VIEW over the authoritative sources, not an independent
          calculator. */}
      <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-900" data-testid="fi-ssot-banner">
        <div className="font-semibold mb-0.5">Financial Integrity — SSOT view layer (v3.21-1687)</div>
        <div>
          Compares the authoritative <b>Compensation Engine</b> result
          (<code>payroll_commissions</code>) against the authoritative
          <b> payout / commission ledger</b> (<code>ptc_ledger</code>)
          for the same Deal.  This screen never computes commission
          independently.  Refresh is diagnostic-only — it does not
          write to payroll, the ledger, or any financial field.
          "Total Discrepancy" = <code>SUM(|payroll − ledger|)</code> across
          open issues; it is a diagnostic prioritization sum, not
          authoritative company financial loss.
        </div>
      </div>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryTile label="Open Issues"       value={summary?.open ?? '—'}         tone="red"     testId="fi-tile-open" />
        <SummaryTile label="Critical"          value={summary?.critical ?? '—'}     tone="red"     testId="fi-tile-critical" />
        <SummaryTile label="Resolved Today"    value={summary?.resolved_today ?? '—'} tone="emerald" testId="fi-tile-resolved-today" />
        <SummaryTile label="Ignored"           value={summary?.ignored ?? '—'}       tone="slate"   testId="fi-tile-ignored" />
        {/* v3.21-1687 · Renamed from "Financial Impact".  The
            underlying metric is SUM(|difference|) across open issues —
            a diagnostic prioritization sum, not authoritative company
            financial loss.  Presenting it as "Financial Impact" was
            misleading. */}
        <SummaryTile label="Total Discrepancy" value={fmtMoney(summary?.total_discrepancy_amount ?? summary?.financial_impact ?? 0)} tone="amber" testId="fi-tile-total-discrepancy" />
        <SummaryTile label="Largest Difference" value={fmtMoney(summary?.largest_difference || 0)} tone="amber" testId="fi-tile-largest" />
      </div>

      {/* Toolbar */}
      <div className="rounded-lg border bg-white p-3 flex flex-wrap items-center gap-2" data-testid="fi-filters">
        <Filter className="h-4 w-4 text-slate-400" />
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="text-xs border rounded px-2 py-1" data-testid="fi-filter-status">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="investigating">Investigating</option>
          <option value="reviewed">Reviewed</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
          <option value="requires_development">Requires Development</option>
        </select>
        <select value={filters.severity} onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}
                className="text-xs border rounded px-2 py-1" data-testid="fi-filter-severity">
          <option value="">All severities</option>
          <option value="red">Critical</option>
          <option value="yellow">Warning</option>
          <option value="green">Minor</option>
        </select>
        <select value={filters.issue_type} onChange={e => setFilters(f => ({ ...f, issue_type: e.target.value }))}
                className="text-xs border rounded px-2 py-1" data-testid="fi-filter-type">
          <option value="">All types</option>
          <option value="COMMISSION_MISMATCH">Commission Mismatch</option>
          <option value="COMMISSION_NOT_IN_LEDGER">Commission Not In Ledger</option>
        </select>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => loadIssues(false)} disabled={loading}
                data-testid="fi-refresh-btn">
          <Search className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
        <Button size="sm" onClick={rerun} disabled={running || loading} data-testid="fi-rerun-btn">
          {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh Financial Audit
        </Button>
      </div>

      {/* Issue list */}
      {loading ? (
        <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading issues…
        </div>
      ) : issues.length === 0 ? (
        <div className="rounded border-2 border-dashed border-emerald-300 bg-emerald-50 p-10 text-center"
             data-testid="fi-empty-state">
          <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <div className="text-lg font-semibold text-emerald-800">No open issues match these filters.</div>
          <div className="text-xs text-emerald-700 mt-1">
            Every audited deal reconciles payroll and ledger cleanly.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map(iss => (
            <IssueCard key={iss.id} issue={iss} onChange={() => { loadIssues(false); loadSummary(); }} />
          ))}
        </div>
      )}
    </div>
  );
}
